import React from 'react';
import Webcam from 'react-webcam';
import { Grid, Button, Box } from '@mui/material';

const defaultDimension = 1000;

export interface WebCamCaptureProps {
	onCapture: (image: string) => void;
	onCancel?: () => void;
	size?: number;
    imageDimension?: number;
	facingMode?: string;
    viewPortPctSize?: number;
}

export function WebCamCapture(props: WebCamCaptureProps) {

   const imageSize = props.size ?? 150;
    const containerSize = imageSize + 2;
    const viewPortPctSize = props.viewPortPctSize ?? 0.75;
    const viewPortSize = Math.min(imageSize * viewPortPctSize);
    const viewPortTop = (containerSize - viewPortSize) / 2;
    const viewPortLeft = (containerSize - viewPortSize) / 2;

    const snapShotSize = props.imageDimension ?? defaultDimension;
    const viewPortSnapShotSize = Math.min(snapShotSize * viewPortPctSize);

	const webcamRef = React.useRef<Webcam>(null);

	const videoConstraints = {
		width: snapShotSize,
        height: snapShotSize,
		facingMode: props.facingMode ?? 'environment',
        aspectRatio: 1,
	};

    const capture = React.useCallback(async () => {
        if (webcamRef !== null && webcamRef.current !== null) {
            const snapshot = webcamRef.current.getScreenshot({
                width: snapShotSize,
                height: snapShotSize,
            });
    
            if (snapshot) {
                const croppedImage = await cropImage(snapshot); // Await the result of cropImage
                props.onCapture(croppedImage);
            }
        }
    }, [webcamRef, props]);

    function cropImage(snapshot: string): Promise<string> {
        return new Promise((resolve) => {
            const image = new Image();
            image.src = snapshot;
    
            image.onload = () => {
                // Create a canvas to crop the image
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
    
                if (!context) {
                    resolve(snapshot); // If context is null, return the original snapshot
                    return;
                }

                const imageWidth = image.width;
                const imageHeight = image.height;
                const centerX = imageWidth / 2;
                const centerY = imageHeight / 2;
    
                // Set canvas dimensions to match the red box
                const cropWidth = viewPortSnapShotSize; 
                const cropHeight = viewPortSnapShotSize; 
                canvas.width = cropWidth;
                canvas.height = cropHeight;
    
                // Calculate the cropping area (centered)
                const offsetX = centerX - (cropWidth / 2);
                const offsetY = centerY - (cropHeight / 2);
    
                // Draw the cropped area onto the canvas
                context.drawImage(
                    image,
                    offsetX,
                    offsetY,
                    cropWidth,
                    cropHeight,
                    0,
                    0,
                    cropWidth,
                    cropHeight
                );
    
                // Get the cropped image as a data URL
                const croppedImage = canvas.toDataURL('image/jpeg');
                resolve(croppedImage); // Resolve the promise with the cropped image
            };
    
            image.onerror = () => {
                resolve(snapshot); // If there's an error loading the image, return the original snapshot
            };
        });
    }

	function cancel() {
		if (props.onCancel) {
			props.onCancel();
		}
	}

	return (
		<Grid container spacing={1} direction="column">
			<Grid item>
				<Box
					sx={{
						position: 'relative',
						border: '1px solid black',
						height: containerSize,
						width: containerSize,
						padding: 1,
					}}
				>
					<Webcam
						audio={false}
						ref={webcamRef}
						height={imageSize}
						width={imageSize}
						screenshotFormat="image/jpeg"
						screenshotQuality={1}
						videoConstraints={videoConstraints}
					/>

					<Box
						style={{
							position: 'absolute',
							top: viewPortTop,
							left: viewPortLeft,
							width: viewPortSize,
							height: viewPortSize,
							border: '2px solid red', // optional border
							pointerEvents: 'none', // so clicks go through to video
						}}
					></Box>
				</Box>
			</Grid>

			<Grid container item spacing={1}>
				<Grid item>
					<Button color="primary" variant="contained" onClick={(e) => capture()}>
						Capture
					</Button>
				</Grid>

				<Grid item>
					<Button color="primary" variant="contained" onClick={(e) => cancel()}>
						Cancel
					</Button>
				</Grid>
			</Grid>
		</Grid>
	);
}
